import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import type { PrismHandle } from "components/Screens/Marketplace/Customer/ProfileSection/glancePrismScene";

// Degrees the prism leans toward the cursor, measured against the card bounds (prototype PRISM_TILT).
const PRISM_TILT = 14;

interface GlancePrismProps {
  /** The glance card: moving over it tilts the prism (prototype data-tilt). */
  cardRef: RefObject<HTMLElement | null>;
}

/** The animated glass prism beside the glance text. Nothing is drawn where WebGL is unavailable. */
function GlancePrism({ cardRef }: GlancePrismProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(typeof window === "undefined" || typeof window.WebGLRenderingContext === "undefined");

  useEffect(() => {
    if (failed) return undefined;
    let handle: PrismHandle | null = null;
    let gone = false;
    import("components/Screens/Marketplace/Customer/ProfileSection/glancePrismScene")
      .then(({ createPrism }) => {
        if (gone || !hostRef.current) return;
        handle = createPrism(hostRef.current);
      })
      .catch(() => {
        if (!gone) setFailed(true);
      });

    const card = cardRef.current;
    const onMove = (e: MouseEvent) => {
      const host = hostRef.current;
      if (!handle || !host || !card) return;
      const r = host.getBoundingClientRect();
      const cardR = card.getBoundingClientRect();
      const cl = (v: number) => Math.max(-1, Math.min(1, v));
      const rad = (PRISM_TILT * Math.PI) / 180;
      const dx = (e.clientX - (r.left + r.width / 2)) / (cardR.width / 2);
      const dy = (e.clientY - (r.top + r.height / 2)) / (cardR.height / 2);
      handle.tilt(cl(dy) * rad, cl(dx) * rad);
      handle.fire(2);
    };
    const onLeave = () => handle?.tilt(0, 0);
    card?.addEventListener("mousemove", onMove);
    card?.addEventListener("mouseleave", onLeave);
    return () => {
      gone = true;
      card?.removeEventListener("mousemove", onMove);
      card?.removeEventListener("mouseleave", onLeave);
      handle?.dispose();
    };
  }, [failed, cardRef]);

  if (failed) return null;
  return (
    <div className="glance__mark">
      <div ref={hostRef} className="glance__prism" role="img" aria-label="AI summary" />
    </div>
  );
}

export default GlancePrism;
